"use client";

import {
  extensionForAudioMimeType,
  isAudioRecordingSupported,
  useAudioRecorder,
} from "@/hooks/useAudioRecorder";
import { captureImpromptuEvent, durationBand } from "@/lib/impromptu/analytics";
import { fetchImpromptuApi } from "@/lib/impromptu/client-api";
import {
  IMPROMPTU_MAX_DURATION_SECONDS,
  IMPROMPTU_MIN_DURATION_SECONDS,
  IMPROMPTU_RECORDING_BITRATE,
  type ImpromptuLanguageCode,
  type ImpromptuLearnerProfile,
  type ImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import {
  IMPROMPTU_COPY,
  IMPROMPTU_LOCAL_FAILURE_COPY,
  type ImpromptuLocalFailure,
  type ImpromptuRecoveryKind,
} from "@/lib/impromptu/copy";
import {
  type ImpromptuAnalyzeError,
  ImpromptuAnalyzeErrorSchema,
  ImpromptuAttemptResultSchema,
  type ImpromptuPronunciationResult,
  type ImpromptuReport,
} from "@/lib/impromptu/types";
import { ImpromptuUploadError, prepareImpromptuUpload } from "@/lib/impromptu/upload";
import type { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

/**
 * One impromptu attempt, from microphone permission to finished report.
 *
 * The phase is a discriminated union rather than a string plus a pile of
 * loosely-related state, because the previous shape allowed combinations that
 * cannot happen: a report phase with a null report (guarded for at every render
 * site), an error with no way to act on it. Each phase now carries exactly the data that phase
 * has, and the reducer ignores actions that do not belong to the current one —
 * so a late reply from an abandoned attempt cannot move the interface.
 */

export type ImpromptuPronunciationState =
  | { status: "idle" }
  | { status: "processing" }
  | ImpromptuPronunciationResult;

export type ImpromptuAttemptFailure = {
  /** For logs and analytics, never shown to the learner. */
  code: string;
  message: string;
  recovery: ImpromptuRecoveryKind;
  /**
   * Present only when the failure carried a wait, so the recovery button can be
   * held until it can actually succeed. Absent means there is nothing to wait
   * for, not that the wait is zero.
   */
  retryAfterSeconds?: number;
};

/**
 * One handler per recoverable failure kind, keyed so a view can look up the
 * action for the failure it is rendering instead of re-deriving the mapping.
 *
 * `none` is excluded rather than mapped to a no-op: a failure that cannot be
 * retried should render no button at all, and a key here would let a no-op button
 * exist.
 */
export type ImpromptuRecoveryHandlers = Record<Exclude<ImpromptuRecoveryKind, "none">, () => void>;

export type ImpromptuAttemptPhase =
  | { kind: "ready" }
  | { kind: "requesting_microphone" }
  | { kind: "recording"; elapsedSeconds: number }
  | { kind: "analysing" }
  | { kind: "report"; report: ImpromptuReport; pronunciation: ImpromptuPronunciationState }
  | { kind: "failed"; failure: ImpromptuAttemptFailure };

type AttemptState = {
  phase: ImpromptuAttemptPhase;
  /**
   * Whether this attempt is a second run at the same prompt. It sits outside the
   * phase union because retry-ness genuinely spans every phase — it changes which
   * event we report at the start and at the end of the same attempt.
   */
  isRetry: boolean;
};

type AttemptAction =
  | { type: "attempt_requested"; isRetry: boolean }
  | { type: "recording_started" }
  | { type: "recording_ticked"; elapsedSeconds: number }
  | { type: "analysis_started" }
  | { type: "report_ready"; report: ImpromptuReport; pronunciationPending: boolean }
  | { type: "pronunciation_changed"; pronunciation: ImpromptuPronunciationState }
  | { type: "failed"; failure: ImpromptuAttemptFailure }
  | { type: "reset" };

const INITIAL_STATE: AttemptState = { phase: { kind: "ready" }, isRetry: false };

function attemptReducer(state: AttemptState, action: AttemptAction): AttemptState {
  switch (action.type) {
    case "attempt_requested":
      return { phase: { kind: "requesting_microphone" }, isRetry: action.isRetry };

    case "recording_started":
      return { ...state, phase: { kind: "recording", elapsedSeconds: 0 } };

    case "recording_ticked":
      if (state.phase.kind !== "recording") return state;
      // The ticker runs twice a second but only whole seconds are displayed, so
      // returning the same state half the time avoids a pointless re-render.
      if (state.phase.elapsedSeconds === action.elapsedSeconds) return state;
      return { ...state, phase: { kind: "recording", elapsedSeconds: action.elapsedSeconds } };

    case "analysis_started":
      return { ...state, phase: { kind: "analysing" } };

    case "report_ready":
      return {
        ...state,
        phase: {
          kind: "report",
          report: action.report,
          pronunciation: action.pronunciationPending
            ? { status: "processing" }
            : { status: "idle" },
        },
      };

    case "pronunciation_changed":
      // Pronunciation finishes after the report by design, so a result that
      // arrives once the learner has moved on has nowhere to go — and should not
      // drag them back to a report they left.
      if (state.phase.kind !== "report") return state;
      return { ...state, phase: { ...state.phase, pronunciation: action.pronunciation } };

    case "failed":
      return { ...state, phase: { kind: "failed", failure: action.failure } };

    case "reset":
      return INITIAL_STATE;
  }
}

/**
 * What each device-side failure leaves the learner able to do.
 *
 * A dropped upload and a dead microphone were previously the same "try again",
 * which asked someone to speak for a minute a second time when we still had
 * their recording in memory.
 */
const LOCAL_FAILURE_RECOVERY: Record<ImpromptuLocalFailure, ImpromptuRecoveryKind> = {
  unsupported_browser: "none",
  microphone_blocked: "restart",
  microphone_lost: "restart",
  recorder_unavailable: "restart",
  recording_failed: "restart",
  recording_too_short: "rerecord",
  network: "resend",
  invalid_report: "resend",
};

/**
 * The route reports `retryable`; this decides what retrying means. A failure in
 * transcription or analysis happened after we had usable audio, so the same bytes
 * can go back up. A recording the route judged too short cannot be rescued by
 * sending it again.
 *
 * `RATE_LIMITED` is a burst the learner only has to wait out, so the recording
 * goes back up unchanged; `PRACTICE_QUOTA_REACHED` is the end of their allowance,
 * where any retry is a button that cannot work.
 */
const ROUTE_FAILURE_RECOVERY: Record<ImpromptuAnalyzeError["code"], ImpromptuRecoveryKind> = {
  IMPROMPTU_DISABLED: "none",
  INVALID_REQUEST: "restart",
  RECORDING_TOO_LARGE: "rerecord",
  RECORDING_TOO_SHORT: "rerecord",
  RATE_LIMITED: "resend",
  PRACTICE_QUOTA_REACHED: "none",
  PRACTICE_UNAVAILABLE: "resend",
  TRANSCRIPTION_UNAVAILABLE: "resend",
  ANALYSIS_UNAVAILABLE: "resend",
};

type UseImpromptuAttemptOptions = {
  languageCode: ImpromptuLanguageCode;
  learnerProfile: ImpromptuLearnerProfile;
  prompt: ImpromptuPromptSelection;
  /** Whether the learner asked for pronunciation *and* it can actually run. */
  pronunciationRequested: boolean;
  /** Whether Gemini may listen to the same temporary WAV used by Azure. */
  geminiAudioRequested: boolean;
  posthog: ReturnType<typeof usePostHog>;
};

export function useImpromptuAttempt({
  languageCode,
  learnerProfile,
  prompt,
  pronunciationRequested,
  geminiAudioRequested,
  posthog,
}: UseImpromptuAttemptOptions) {
  const [state, dispatch] = useReducer(attemptReducer, INITIAL_STATE);
  const attemptIdRef = useRef<string | null>(null);
  /**
   * The recording is kept after submitting so a failure on our side can be
   * retried without asking the learner to speak again. Held in a ref rather than
   * in phase state because a Blob is not something to put in a state snapshot we
   * might one day want to log.
   */
  const lastRecordingRef = useRef<{ blob: Blob; durationMs: number } | null>(null);
  const analyzeControllerRef = useRef<AbortController | null>(null);
  const pronunciationViewedAttemptRef = useRef<string | null>(null);

  const capture = useCallback(
    (event: string, properties: Record<string, unknown> = {}) => {
      captureImpromptuEvent(posthog, event, {
        language_code: languageCode,
        learner_profile: learnerProfile,
        ...properties,
      });
    },
    [languageCode, learnerProfile, posthog]
  );

  const fail = useCallback((failure: ImpromptuAttemptFailure) => {
    // Offering to resend without a recording to send would be a dead button.
    const recovery =
      failure.recovery === "resend" && !lastRecordingRef.current ? "restart" : failure.recovery;
    dispatch({ type: "failed", failure: { ...failure, recovery } });
  }, []);

  const failLocally = useCallback(
    (code: ImpromptuLocalFailure) => {
      fail({
        code,
        message: IMPROMPTU_LOCAL_FAILURE_COPY[code],
        recovery: LOCAL_FAILURE_RECOVERY[code],
      });
    },
    [fail]
  );

  const recorder = useAudioRecorder({
    // A recorder that dies mid-attempt is reported here rather than thrown,
    // because at that moment nothing is awaiting it.
    onFailure: failLocally,
    audioBitsPerSecond: IMPROMPTU_RECORDING_BITRATE,
  });

  const abortRequests = useCallback(() => {
    analyzeControllerRef.current?.abort();
    analyzeControllerRef.current = null;
  }, []);

  const cancelAttempt = useCallback(() => {
    abortRequests();
    recorder.cancel();
    attemptIdRef.current = null;
    lastRecordingRef.current = null;
  }, [abortRequests, recorder]);

  useEffect(() => cancelAttempt, [cancelAttempt]);

  const submitRecording = useCallback(
    async (recording: { blob: Blob; durationMs: number }, attemptId: string) => {
      const { blob, durationMs } = recording;
      lastRecordingRef.current = recording;
      dispatch({ type: "analysis_started" });

      const controller = new AbortController();
      analyzeControllerRef.current = controller;
      const mimeType = blob.type || "audio/webm";
      const formData = new FormData();
      formData.append("attemptId", attemptId);
      formData.append("promptId", prompt.id);
      formData.append("promptText", prompt.text);
      formData.append("promptSource", prompt.source);
      formData.append("promptCategory", prompt.category);
      formData.append("languageCode", languageCode);
      formData.append("learnerProfile", learnerProfile);
      formData.append("durationMs", String(Math.round(durationMs)));
      if (geminiAudioRequested) formData.append("geminiAudioRequested", "true");
      if (pronunciationRequested) formData.append("pronunciationRequested", "true");

      capture("impromptu_attempt_submitted", {
        attempt_id: attemptId,
        prompt_id: prompt.id,
        prompt_category: prompt.category,
        prompt_source: prompt.source,
        duration_band: durationBand(durationMs / 1_000),
      });

      try {
        const upload = await prepareImpromptuUpload(
          formData,
          blob,
          `impromptu-attempt.${extensionForAudioMimeType(mimeType)}`
        );
        if (attemptIdRef.current !== attemptId || controller.signal.aborted) return;
        capture("impromptu_audio_upload_prepared", {
          attempt_id: attemptId,
          original_bytes: blob.size,
          body_bytes: upload.body.size,
          recording_mime_type: mimeType,
          single_wav_fallback: upload.converted,
        });
        const response = await fetchImpromptuApi("/api/impromptu/analyze", {
          method: "POST",
          body: upload.body,
          headers: { "Content-Type": upload.contentType },
          signal: controller.signal,
        });
        // Infrastructure errors can be plain text/HTML (not our JSON schema).
        if (attemptIdRef.current !== attemptId) return;
        if (response.status === 413) {
          fail({
            code: "RECORDING_TOO_LARGE",
            message: "That recording is too large to upload. Please record again.",
            recovery: "rerecord",
          });
          capture("impromptu_report_failed", {
            attempt_id: attemptId,
            provider_status: "payload_too_large",
            http_status: 413,
            body_bytes: upload.body.size,
          });
          return;
        }
        const contentType = response.headers.get("content-type") || "";
        const payload: unknown = contentType.includes("application/json")
          ? await response.json().catch(() => null)
          : null;
        if (attemptIdRef.current !== attemptId || controller.signal.aborted) return;

        if (!response.ok) {
          // The route already decided whether this can be retried and worded the
          // failure for the learner. Parsing its own schema rather than picking
          // fields out by hand is what keeps the two from disagreeing — the hand
          // written version inverted the rate-limit case.
          const parsedError = ImpromptuAnalyzeErrorSchema.safeParse(payload);
          const failure: ImpromptuAttemptFailure = parsedError.success
            ? {
                code: parsedError.data.code,
                message: parsedError.data.error,
                recovery: parsedError.data.retryable
                  ? ROUTE_FAILURE_RECOVERY[parsedError.data.code]
                  : "none",
                retryAfterSeconds: parsedError.data.retryAfterSeconds,
              }
            : response.status === 429
              ? {
                  code: "RATE_LIMITED",
                  message: "A few attempts arrived too quickly. Wait a moment and try again.",
                  recovery: "resend",
                  retryAfterSeconds: Number(response.headers.get("retry-after")) || undefined,
                }
              : {
                  code: "UNRECOGNISED_ERROR",
                  message: IMPROMPTU_COPY.errors.unknownFailure,
                  recovery: "resend",
                };
          capture("impromptu_report_failed", {
            attempt_id: attemptId,
            prompt_id: prompt.id,
            provider_status: failure.code,
            recovery: failure.recovery,
            http_status: response.status,
          });
          fail(failure);
          return;
        }

        const parsed = ImpromptuAttemptResultSchema.safeParse(payload);
        if (!parsed.success) throw new Error("invalid_report");

        dispatch({
          type: "report_ready",
          report: parsed.data.report,
          pronunciationPending: false,
        });
        const pronunciation = parsed.data.pronunciation;
        if (pronunciation) {
          dispatch({ type: "pronunciation_changed", pronunciation });
          if (pronunciation.status === "available") {
            capture("impromptu_pronunciation_available", {
              attempt_id: attemptId,
              provider_status: "available",
            });
          }
        }

        capture(state.isRetry ? "impromptu_retry_completed" : "impromptu_report_ready", {
          attempt_id: attemptId,
          prompt_id: prompt.id,
          prompt_category: prompt.category,
          duration_band: durationBand(durationMs / 1_000),
          rubric_version: parsed.data.report.rubricVersion,
          provider_status: "ready",
        });
      } catch (cause) {
        if (controller.signal.aborted || attemptIdRef.current !== attemptId) return;
        if (cause instanceof ImpromptuUploadError) {
          fail({
            code: "RECORDING_TOO_LARGE",
            message: "We could not prepare this recording for upload. Please record again.",
            recovery: "rerecord",
          });
          capture("impromptu_report_failed", {
            attempt_id: attemptId,
            provider_status: "payload_too_large",
            original_bytes: blob.size,
            recording_mime_type: mimeType,
          });
          return;
        }
        const code: ImpromptuLocalFailure =
          cause instanceof TypeError ? "network" : "invalid_report";
        capture("impromptu_report_failed", {
          attempt_id: attemptId,
          prompt_id: prompt.id,
          provider_status: code === "network" ? "network_error" : "invalid_report",
          recovery: LOCAL_FAILURE_RECOVERY[code],
        });
        failLocally(code);
      } finally {
        if (analyzeControllerRef.current === controller) analyzeControllerRef.current = null;
      }
    },
    [
      capture,
      fail,
      failLocally,
      languageCode,
      learnerProfile,
      prompt.category,
      prompt.id,
      prompt.source,
      prompt.text,
      geminiAudioRequested,
      pronunciationRequested,
      state.isRetry,
    ]
  );

  const finishRecording = useCallback(async () => {
    const attemptId = attemptIdRef.current;
    if (!attemptId) return;
    const result = await recorder.stop();
    if (attemptIdRef.current !== attemptId) return;
    if (!result.ok) {
      failLocally(result.code);
      return;
    }
    if (result.durationMs < IMPROMPTU_MIN_DURATION_SECONDS * 1_000 || result.blob.size === 0) {
      failLocally("recording_too_short");
      return;
    }
    await submitRecording({ blob: result.blob, durationMs: result.durationMs }, attemptId);
  }, [failLocally, recorder, submitRecording]);

  const beginRecording = useCallback(
    (isRetry = state.isRetry) => {
      const started = recorder.start();
      if (!started.ok) {
        failLocally(started.code);
        return;
      }
      dispatch({ type: "recording_started" });
      capture(isRetry ? "impromptu_retry_started" : "impromptu_attempt_started", {
        attempt_id: attemptIdRef.current,
        prompt_id: prompt.id,
        prompt_category: prompt.category,
        prompt_source: prompt.source,
      });
    },
    [capture, failLocally, prompt.category, prompt.id, prompt.source, recorder, state.isRetry]
  );

  /**
   * Opens the microphone and starts recording as soon as permission is granted.
   *
   * `reuseAttemptId` matters for money: a retry after a server-side failure has
   * to arrive under the same attempt id, or the learner's practice quota is
   * charged twice for one minute of speaking.
   */
  const armAttempt = useCallback(
    async ({ isRetry, reuseAttemptId }: { isRetry: boolean; reuseAttemptId: boolean }) => {
      if (!isAudioRecordingSupported()) {
        failLocally("unsupported_browser");
        return;
      }
      const attemptId =
        reuseAttemptId && attemptIdRef.current ? attemptIdRef.current : crypto.randomUUID();
      abortRequests();
      dispatch({ type: "attempt_requested", isRetry });
      const armed = await recorder.arm();
      if (!armed.ok) {
        failLocally(armed.code);
        return;
      }
      attemptIdRef.current = attemptId;
      lastRecordingRef.current = null;
      beginRecording(isRetry);
    },
    [abortRequests, beginRecording, failLocally, recorder]
  );

  const startAttempt = useCallback(
    () => armAttempt({ isRetry: false, reuseAttemptId: false }),
    [armAttempt]
  );

  const retryPrompt = useCallback(
    () => armAttempt({ isRetry: true, reuseAttemptId: false }),
    [armAttempt]
  );

  /** Record again for the same attempt, so the quota is not charged twice. */
  const recordAgain = useCallback(
    () => armAttempt({ isRetry: state.isRetry, reuseAttemptId: true }),
    [armAttempt, state.isRetry]
  );

  /** Send the recording we already have, without asking the learner to repeat it. */
  const resendRecording = useCallback(async () => {
    const recording = lastRecordingRef.current;
    const attemptId = attemptIdRef.current;
    if (!recording || !attemptId) {
      failLocally("recording_failed");
      return;
    }
    capture("impromptu_report_resent", { attempt_id: attemptId, prompt_id: prompt.id });
    await submitRecording(recording, attemptId);
  }, [capture, failLocally, prompt.id, submitRecording]);

  const stopRecording = useCallback(() => {
    void finishRecording();
  }, [finishRecording]);

  /**
   * A fresh attempt on the same prompt, for failures that left no usable audio.
   *
   * Keeps the current retry flag rather than asserting one: a blocked microphone
   * is not a second reading of the same prompt, and counting it as a retry would
   * inflate the one number that tells us whether people actually speak twice.
   */
  const restartAttempt = useCallback(
    () => armAttempt({ isRetry: state.isRetry, reuseAttemptId: false }),
    [armAttempt, state.isRetry]
  );

  const reset = useCallback(() => {
    cancelAttempt();
    dispatch({ type: "reset" });
  }, [cancelAttempt]);

  /**
   * The failure view reads its one action straight out of this map, so the
   * decision about what a given failure means was made once, in the recovery
   * tables above, rather than again in a chain of conditionals in the markup.
   * `none` is absent by construction: there is nothing to offer, so there is no
   * handler to accidentally wire up to a button.
   */
  const recovery = useMemo<ImpromptuRecoveryHandlers>(
    () => ({
      resend: () => void resendRecording(),
      rerecord: () => void recordAgain(),
      restart: () => void restartAttempt(),
    }),
    [recordAgain, resendRecording, restartAttempt]
  );

  // Recording clock, sampled twice a second so the displayed second is never
  // more than half a second stale, and the hard limit is enforced close to it.
  useEffect(() => {
    if (state.phase.kind !== "recording") return;
    const timer = window.setInterval(() => {
      const elapsedSeconds = Math.floor(recorder.elapsedMs() / 1_000);
      dispatch({ type: "recording_ticked", elapsedSeconds });
      if (elapsedSeconds >= IMPROMPTU_MAX_DURATION_SECONDS) void finishRecording();
    }, 500);
    return () => window.clearInterval(timer);
  }, [finishRecording, recorder, state.phase.kind]);

  // Reported once per attempt: pronunciation arrives after the report, so an
  // effect keyed on the result alone would fire again on every unrelated render.
  useEffect(() => {
    if (state.phase.kind !== "report") return;
    const { pronunciation } = state.phase;
    if (pronunciation.status !== "available") return;
    if (pronunciationViewedAttemptRef.current === pronunciation.attemptId) return;
    pronunciationViewedAttemptRef.current = pronunciation.attemptId;
    capture("impromptu_pronunciation_viewed", { attempt_id: pronunciation.attemptId });
  }, [capture, state.phase]);

  return {
    phase: state.phase,
    getRecordingStream: recorder.getStream,
    isRetry: state.isRetry,
    startAttempt,
    retryPrompt,
    recordAgain,
    resendRecording,
    stopRecording,
    cancelAttempt,
    reset,
    recovery,
  };
}
